import React, { useState, useEffect, createContext, useContext } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  Users,
  UserCheck,
  ShoppingBag,
  Package,
  FileText,
  Settings,
  Activity,
  CreditCard,
  MoreVertical,
  User,
  Bell,
  LogOut,
  Menu,
  X,
  Lock,
  Search,
  PanelLeft,
  Command,
  Building2,
  ClipboardList,
  ClipboardCheck,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { APP_VERSION, getFooterText } from '@/config/version';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';
import { IntelliHealthChatDrawer } from '@/components/intellihealth/chat';
import PaymentAccountConfigAlert from '@/components/clinic/PaymentAccountConfigAlert';
import NotificationCenter from '@/components/clinic/NotificationCenter';
import { usePendingIntakes } from '@/hooks/usePendingIntakes';
import Breadcrumbs from '@/components/ui/breadcrumb';

// Create context for sidebar state
export const SidebarContext = createContext<{
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
  isMobileOpen: boolean;
  setIsMobileOpen: (value: boolean) => void;
}>({
  isCollapsed: false,
  setIsCollapsed: () => { },
  isMobileOpen: false,
  setIsMobileOpen: () => { },
});

export const useSidebar = () => useContext(SidebarContext);

const ClinicStaffPortal = () => {
  const { user, logout } = useAuth();
  const { rolePermissions, clinic, isAdmin, isPractitioner, isPractitionerStaff, isClinicStaff, isGuided, isDirect } = useClinic();
  const { newResponsesCount: intakesNewCount } = usePendingIntakes(clinic?.id);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Close mobile menu on window resize to desktop and track mobile state
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileOpen(false);
      }
    };
    handleResize(); // Check on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getUserInitials = () => {
    if (user?.firstName) {
      return user.firstName[0].toUpperCase();
    }
    if (user?.name) {
      return user.name[0].toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'S';
  };

  const getDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.name) return user.name;
    if (user?.firstName) return user.firstName;
    return 'Staff';
  };

  const handleSignOut = async () => {
    try {
      await logout();
      toast({
        title: 'Success',
        description: 'Successfully signed out!',
        variant: 'success',
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to sign out. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Check if user has only admin access (no practitioner/staff roles)
  const isAdminOnly = isAdmin && !isPractitioner && !isPractitionerStaff && !isClinicStaff;

  const allMenuItems = [
    { id: 'dashboard', title: 'Dashboard', icon: Activity, path: '/clinic-staff-portal', permission: null },
    { id: 'patients:view', title: 'Patients', icon: Users, path: '/clinic-staff-portal/patients', permission: 'patients:view' },
    { id: 'intakes:view', title: isGuided ? 'Enrollments' : 'Intakes', icon: ClipboardList, path: '/clinic-staff-portal/intakes', permission: 'patients:view', badgeCount: intakesNewCount, badgeColor: 'bg-blue-500', hideForAdminOnly: true },
    { id: 'prescriptions:view', title: 'Orders', icon: FileText, path: '/clinic-staff-portal/prescriptions', permission: 'prescriptions:view' },
    { id: 'practitioners:view', title: 'Practitioners', icon: UserCheck, path: '/clinic-staff-portal/practitioners', permission: 'practitioners:view' },
    { id: 'practitioner_staff:view', title: 'Practitioner Staffs', icon: Users, path: '/clinic-staff-portal/practitioner-staffs', permission: 'practitioner_staff:view' },
    { id: 'products:view', title: 'Products', icon: Package, path: '/clinic-staff-portal/products', permission: 'products:view' },
    { id: 'storefront:view', title: 'Storefront', icon: ShoppingBag, path: '/clinic-staff-portal/storefront', permission: 'storefront:view' },
    { id: 'guided-orders', title: 'Guided Orders', icon: ClipboardCheck, path: '/clinic-staff-portal/guided-orders', permission: 'storefront:view', showOnlyForGuided: true },
    { id: 'payments:view', title: 'Payments', icon: CreditCard, path: '/clinic-staff-portal/payments', permission: 'payments:view' },
  ];

  // Filter menu items based on permissions and user roles
  const menuItems = allMenuItems.filter(item => {
    // Hide items marked for admin-only users
    if (item.hideForAdminOnly && isAdminOnly) {
      return false;
    }
    if ((item as { showOnlyForGuided?: boolean }).showOnlyForGuided && isDirect) {
      return false;
    }
    return !item.permission || rolePermissions.includes(item.permission);
  });

  const isPathActive = (path: string) => {
    if (path === '/clinic-staff-portal') return location.pathname === '/clinic-staff-portal';
    return location.pathname.startsWith(path);
  };

  // Get active user roles with their labels and colors
  const getActiveRoles = () => {
    const roles: Array<{ key: string; label: string; colorClass: string }> = [];

    if (isAdmin) {
      roles.push({
        key: 'clinic_admin',
        label: 'Clinic Admin',
        colorClass: 'bg-violet-100 text-violet-800 border-violet-300',
      });
    }
    if (isPractitioner) {
      roles.push({
        key: 'provider',
        label: 'Provider',
        colorClass: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      });
    }
    if (isPractitionerStaff) {
      roles.push({
        key: 'staff_practitioner',
        label: 'Staff Practitioner',
        colorClass: 'bg-amber-100 text-amber-800 border-amber-300',
      });
    }
    if (isClinicStaff) {
      roles.push({
        key: 'clinic_staff',
        label: 'Clinic Staff',
        colorClass: 'bg-sky-100 text-sky-800 border-sky-300',
      });
    }

    return roles;
  };

  const activeRoles = getActiveRoles();

  return (
    <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen }}>
      <TooltipProvider delayDuration={0}>
        <div className="h-screen flex bg-background">
          {/* Mobile Overlay */}
          {isMobileOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-[70] lg:hidden"
              onClick={() => setIsMobileOpen(false)}
            />
          )}

          {/* Sidebar */}
          <div
            className={`
              fixed lg:relative inset-y-0 left-0 z-[80]
              bg-sidebar text-sidebar-foreground border-r border-sidebar-border flex flex-col transition-all duration-300 ease-in-out
              ${isCollapsed ? 'lg:w-16' : 'lg:w-64'}
              ${isMobileOpen ? 'w-64 translate-x-0' : 'w-64 -translate-x-full lg:translate-x-0'}
            `}
          >
            {/* Logo/Brand Area */}
            <div className={`flex-none border-b border-sidebar-border ${isCollapsed ? 'p-3' : 'p-4'}`}>
              <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
                {isCollapsed ? (
                  <img
                    src="/nimbus_logo_white.svg"
                    alt="Nimbus"
                    className="h-7 w-auto cursor-pointer"
                    style={{ maxWidth: '28px', objectFit: 'cover', objectPosition: 'left' }}
                    onClick={() => navigate('/clinic-staff-portal')}
                  />
                ) : (
                  <>
                    <img src="/nimbus_logo_white.svg" alt="Nimbus" className="h-7 w-auto cursor-pointer" onClick={() => navigate('/clinic-staff-portal')} />
                    <button
                      onClick={() => setIsMobileOpen(false)}
                      className="lg:hidden p-1.5 rounded-md hover:bg-sidebar-accent transition-colors"
                    >
                      <X className="h-5 w-5 text-sidebar-foreground/60" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Clinic Name Section */}
            <div className={`flex-none border-b border-sidebar-border ${isCollapsed ? 'px-2 py-3' : 'px-3 py-3'}`}>
              <div
                className={`flex items-center gap-3 rounded-lg ${isCollapsed ? 'justify-center p-2' : 'bg-sidebar-accent p-3'}`}
              >
                {isCollapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-full h-8 rounded-md flex items-center justify-center bg-brand-teal/20">
                        <Building2 className="w-4 h-4 text-brand-teal" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="font-medium text-xs border-slate-700 bg-slate-800 text-white">
                      {user?.clinic?.name || 'N/A'}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <>
                    <div className="flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center bg-brand-teal/20">
                      <Building2 className="w-4 h-4 text-brand-teal" />
                    </div>
                    <div className="flex-1 min-w-0" style={{ lineHeight: 1 }}>
                      <span className="text-[10px] font-medium uppercase tracking-wide block text-sidebar-foreground/60">
                        Clinic
                      </span>
                      <p className="font-semibold text-white text-sm truncate">
                        {user?.clinic?.name || 'N/A'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex-1 py-3 overflow-y-auto scrollbar-hide">
              {!isCollapsed && (
                <div className="px-3 py-2">
                  <span className="text-[11px] font-semibold tracking-wide text-sidebar-foreground/40">
                    Navigations
                  </span>
                </div>
              )}
              <nav className="space-y-0.5 px-2">
                {menuItems.map(item => {
                  const Icon = item.icon;
                  const isActive = isPathActive(item.path);

                  if (isCollapsed) {
                    return (
                      <Tooltip key={item.id}>
                        <TooltipTrigger asChild>
                          <Link
                            to={item.path}
                            className={`relative flex items-center justify-center p-2 rounded-md transition-all duration-150 ${isActive
                              ? 'bg-brand-teal text-white shadow-sm'
                              : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground'
                              }`}
                          >
                            <Icon className={`h-4 w-4 ${isActive ? 'text-white' : ''}`} strokeWidth={isActive ? 2.5 : 2} />
                            {item.badgeCount !== undefined && item.badgeCount > 0 && (
                              <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[14px] h-[14px] px-0.5 text-[8px] font-bold rounded-full bg-red-500 text-white ring-2 ring-sidebar">
                                {item.badgeCount > 9 ? '9+' : item.badgeCount}
                              </span>
                            )}
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="font-medium bg-slate-900 text-white border-slate-800">
                          {item.title}
                        </TooltipContent>
                      </Tooltip>
                    );
                  }

                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={`relative flex items-center justify-between px-3 py-[7px] rounded-md font-medium transition-all duration-150 text-[12px] group ${isActive
                        ? 'bg-brand-teal text-white shadow-sm'
                        : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground'
                        }`}
                    >
                      <div className="flex items-center gap-3 ml-0.5">
                        <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-white' : 'text-sidebar-foreground/60'}`} strokeWidth={isActive ? 2.5 : 1.8} />
                        <span>{item.title}</span>
                      </div>
                      {item.badgeCount !== undefined && item.badgeCount > 0 && (
                        <span className={`flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full text-white ${isActive ? 'bg-white/20' : 'bg-red-500'}`}>
                          {item.badgeCount > 99 ? '99+' : item.badgeCount}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Sidebar Footer - User Profile */}
            <div className="flex-none">
              {!isCollapsed && (
                <div className="px-4 pb-2">
                  <p className="text-[10px] text-sidebar-foreground/25">
                    {getFooterText()} | V{APP_VERSION}
                  </p>
                </div>
              )}
              <div
                className={`border-t border-sidebar-border flex items-center ${isCollapsed ? 'h-[58px] p-2' : 'h-[64px] px-3 py-2'}`}
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    {isCollapsed ? (
                      <button
                        className="flex justify-center items-center w-full h-full rounded-md transition-colors hover:bg-sidebar-accent"
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs font-medium bg-sidebar-primary text-sidebar-primary-foreground">
                            {getUserInitials()}
                          </AvatarFallback>
                        </Avatar>
                      </button>
                    ) : (
                      <button
                        className="flex items-center justify-between w-full p-2 rounded-md transition-colors gap-2 hover:bg-sidebar-accent"
                      >
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          <Avatar className="h-9 w-9 flex-shrink-0">
                            <AvatarFallback className="text-sm font-medium bg-sidebar-primary text-sidebar-primary-foreground">
                              {getUserInitials()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1 text-left">
                            <p className="font-medium truncate text-sidebar-foreground text-[0.8rem]">
                              {getDisplayName()}
                            </p>
                            <p className="truncate max-w-[140px] text-sidebar-foreground/50 text-[0.7rem]">
                              {user?.email || ''}
                            </p>
                          </div>
                        </div>
                        <MoreVertical className="h-4 w-4 flex-shrink-0 ml-1 text-sidebar-foreground/50" />
                      </button>
                    )}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" side={isMobile ? "top" : "right"} className={`w-56 bg-white border border-slate-200 shadow-lg ${isMobile ? 'mb-2' : 'ml-2'}`}>
                    <div className="flex items-center space-x-3 p-3 border-b border-slate-100">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="text-sm font-medium" style={{ backgroundColor: '#231f20', color: '#fff' }}>
                          {getUserInitials()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: '#28282C' }}>{getDisplayName()}</p>
                        <p className="text-xs truncate" style={{ color: '#6E6E73' }}>{user?.email || ''}</p>
                      </div>
                    </div>

                    <div className="py-1">
                      <DropdownMenuItem onClick={() => setShowChangePasswordDialog(true)} className="cursor-pointer px-3 py-2 hover:bg-slate-100" style={{ fontSize: '13px' }}>
                        <Lock className="h-4 w-4 mr-3" style={{ color: '#6E6E73' }} strokeWidth={2} />
                        <span style={{ color: '#28282C' }}>Change Password</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/clinic-staff-portal/preferences')} className="cursor-pointer px-3 py-2 hover:bg-slate-100" style={{ fontSize: '13px' }}>
                        <Settings className="h-4 w-4 mr-3" style={{ color: '#6E6E73' }} strokeWidth={2} />
                        <span style={{ color: '#28282C' }}>Preferences</span>
                      </DropdownMenuItem>
                    </div>

                    <DropdownMenuSeparator className="bg-slate-100" />

                    <div className="py-1">
                      <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer px-3 py-2 hover:bg-slate-100" style={{ fontSize: '13px' }}>
                        <LogOut className="h-4 w-4 mr-3 text-red-500" strokeWidth={2} />
                        <span className="text-red-500">Sign Out</span>
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden bg-background min-w-0">
            {/* Header */}
            <div className="flex-none">
              {/* Mobile Header */}
              <div className="lg:hidden flex items-center justify-between px-3 py-2.5 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <button onClick={() => setIsMobileOpen(true)} className="p-2 rounded-md hover:bg-slate-100">
                    <Menu className="h-5 w-5 text-slate-600" />
                  </button>
                  <img src="/nimbus_logo.svg" alt="Nimbus Healthcare" className="h-5 w-auto cursor-pointer" onClick={() => navigate('/clinic-staff-portal')} />
                </div>
                <div className="flex items-center gap-1">
                  {/* Role Badges - Mobile */}
                  {activeRoles.length > 0 && (
                    <div className="flex items-center gap-1 mr-1">
                      {activeRoles.map((role) => (
                        <Badge
                          key={role.key}
                          variant="outline"
                          className={`flex items-center px-1.5 py-0.5 text-[10px] font-medium ${role.colorClass}`}
                        >
                          {role.label}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <NotificationCenter />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full p-0">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs font-medium" style={{ backgroundColor: '#231f20', color: '#fff' }}>
                            {getUserInitials()}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 bg-white border border-slate-200 shadow-lg p-3">
                      <div className="px-2 py-1.5 border-b border-slate-100 mb-2 pb-2">
                        <p className="text-sm font-medium text-slate-900 truncate">{getDisplayName()}</p>
                        <p className="text-xs text-slate-500 truncate">{user?.email || ''}</p>
                      </div>
                      <DropdownMenuItem onClick={() => setShowChangePasswordDialog(true)} className="text-slate-700 hover:text-slate-900 hover:bg-slate-50 cursor-pointer" style={{ fontSize: '13px' }}>
                        <Lock className="h-4 w-4 mr-2 text-slate-500" strokeWidth={2} />
                        Change Password
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/clinic-staff-portal/preferences')} className="text-slate-700 hover:text-slate-900 hover:bg-slate-50 cursor-pointer" style={{ fontSize: '13px' }}>
                        <Settings className="h-4 w-4 mr-2 text-slate-500" strokeWidth={2} />
                        Preferences
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-slate-200 my-2" />
                      <DropdownMenuItem className="text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer" onClick={handleSignOut} style={{ fontSize: '13px' }}>
                        <LogOut className="h-4 w-4 mr-2" strokeWidth={2} />
                        Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Desktop Header */}
              <header className="bg-white border-b border-slate-200 sticky top-0 z-50 hidden lg:block">
                <div className="px-4 py-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100 text-slate-700" onClick={() => setIsCollapsed(!isCollapsed)}>
                        <PanelLeft className="h-4 w-4" strokeWidth={2} />
                      </Button>
                      <div className="h-5 w-px bg-slate-200" />
                      <button
                        type="button"
                        className="relative flex items-center h-9 pl-9 pr-12 border border-slate-200 rounded-md bg-[#f8f3ed] hover:bg-[#f0ebe4] hover:border-slate-300 transition-colors duration-200 cursor-pointer"
                        style={{ minWidth: '340px' }}
                      >
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={2} />
                        <span className="text-sm text-slate-400">Search...</span>
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-slate-200 bg-white">
                          <Command className="h-3 w-3 text-slate-400" strokeWidth={2} />
                          <span className="text-xs text-slate-400 font-medium">K</span>
                        </div>
                      </button>
                    </div>
                    <div className="flex items-center space-x-1.5">
                      {activeRoles.length > 0 && (
                        <>
                          <div className="flex items-center gap-2">
                            {activeRoles.map((role) => (
                              <Badge
                                key={role.key}
                                variant="outline"
                                className={`px-2.5 py-1 text-xs font-medium ${role.colorClass}`}
                              >
                                {role.label}
                              </Badge>
                            ))}
                          </div>
                          <div className="h-5 w-px bg-slate-200 mx-0.5" />
                        </>
                      )}
                      <NotificationCenter />
                      <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100 text-slate-500" onClick={() => navigate('/clinic-staff-portal/settings')}>
                        <Settings className="h-4 w-4" strokeWidth={2} />
                      </Button>
                    </div>
                  </div>
                </div>
              </header>

              {/* Breadcrumbs — hidden on dashboard */}
              {location.pathname !== '/clinic-staff-portal' && (
                <div className="hidden border-b border-slate-100 bg-white px-4 sm:px-6 lg:px-8 py-1.5 lg:block">
                  <Breadcrumbs />
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              <div className="mx-auto max-w-[2000px] p-4 sm:p-6 lg:p-8">
                <PaymentAccountConfigAlert className="mb-2" variant="staff" />
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </TooltipProvider>

      <ChangePasswordDialog open={showChangePasswordDialog} onOpenChange={setShowChangePasswordDialog} />

      <IntelliHealthChatDrawer />
    </SidebarContext.Provider>
  );
};

export default ClinicStaffPortal;
